import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { GraduationCap, Plus, Award, Play, CheckCircle2 } from "lucide-react";
import { COUNTRIES } from "@/lib/i18n";
import { toast } from "sonner";

export default function Courses() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [country, setCountry] = useState(user?.country || "FR");
  const [enrollments, setEnrollments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", duration_hours: 2, modules: ["Introduction"], country: user?.country || "FR", language: user?.language || "fr" });
  const [openCourse, setOpenCourse] = useState(null);

  const load = async () => {
    try {
      const [{ data: cs }, { data: enrs }] = await Promise.all([
        api.get("/courses", { params: { country } }),
        api.get("/courses/enrollments/me").catch(() => ({ data: [] })),
      ]);
      setCourses(cs);
      setEnrollments(enrs);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [country]);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, modules: form.modules.filter((m) => m.trim()) };
      await api.post("/courses", payload);
      toast.success("Cours créé");
      setOpen(false);
      setForm({ title: "", description: "", duration_hours: 2, modules: ["Introduction"], country, language: "fr" });
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const enroll = async (courseId) => {
    try {
      await api.post(`/courses/${courseId}/enroll`);
      toast.success("Inscrit au cours");
      load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const completeModule = async (courseId, idx) => {
    try {
      await api.post(`/courses/${courseId}/complete/${idx}`);
      toast.success("Module marqué comme terminé");
      load();
      const { data: enr } = await api.get("/courses/enrollments/me");
      const updated = enr.find((e) => e.course_id === courseId);
      setOpenCourse((c) => c ? { ...c, _enr: updated } : null);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    }
  };

  const myEnr = (cid) => enrollments.find((e) => e.course_id === cid);
  const isDoctor = user?.role === "doctor";

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-deep">{t("courses.title")}</h1>
          <p className="text-slate-500 mt-1">Formation continue par et pour les professionnels du réseau.</p>
        </div>
        <div className="flex gap-2">
          <Select value={country} onValueChange={setCountry}>
            <SelectTrigger className="w-40 h-10" data-testid="courses-country-select"><SelectValue /></SelectTrigger>
            <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
          </Select>
          {isDoctor && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="btn-coral" data-testid="create-course-btn"><Plus className="h-4 w-4 me-1" />{t("courses.create")}</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>{t("courses.create")}</DialogTitle></DialogHeader>
                <form onSubmit={create} className="space-y-3 mt-2">
                  <div><Label>Titre</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title-input" /></div>
                  <div><Label>Description</Label><Textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="course-desc-input" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Durée (h)</Label><Input type="number" min="1" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: parseInt(e.target.value) || 1 })} /></div>
                    <div><Label>Pays</Label>
                      <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c.code} value={c.code}>{c.flag} {c.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Modules (un par ligne)</Label>
                    <Textarea rows={4} value={form.modules.join("\n")} onChange={(e) => setForm({ ...form, modules: e.target.value.split("\n") })} data-testid="course-modules-input" />
                  </div>
                  <Button type="submit" className="w-full btn-primary" data-testid="course-submit-btn">{t("courses.create")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-slate-300 rounded-xl text-slate-500">
          <GraduationCap className="h-10 w-10 mx-auto text-slate-300 mb-3" />
          {t("courses.no_courses")}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {courses.map((c) => {
            const enr = myEnr(c.course_id);
            return (
              <article key={c.course_id} className="bg-white border border-slate-200 rounded-xl p-5 card-hover" data-testid={`course-${c.course_id}`}>
                <div className="flex items-start gap-2">
                  <GraduationCap className="h-6 w-6 text-[#618DB8] flex-shrink-0" />
                  <div>
                    <h3 className="font-display font-bold text-deep">{c.title}</h3>
                    <div className="text-xs text-slate-500 mt-0.5">{c.duration_hours}h · {c.modules?.length || 0} {t("courses.modules")}</div>
                  </div>
                </div>
                <p className="mt-3 text-sm text-slate-600 line-clamp-3">{c.description}</p>
                {enr && (
                  <div className="mt-3">
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#618DB8] to-[#FF7A5C]" style={{ width: `${enr.progress}%` }} />
                    </div>
                    <div className="text-xs text-slate-500 mt-1">{enr.progress}% · {enr.certificate_issued_at && <span className="text-emerald-700 font-bold flex items-center gap-1"><Award className="h-3 w-3" />Certifié</span>}</div>
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  {!enr ? (
                    <Button size="sm" onClick={() => enroll(c.course_id)} className="btn-coral" data-testid={`enroll-${c.course_id}`}>{t("courses.enroll")}</Button>
                  ) : (
                    <Button size="sm" onClick={() => setOpenCourse({ ...c, _enr: enr })} className="btn-primary" data-testid={`open-${c.course_id}`}><Play className="h-3 w-3 me-1" />Continuer</Button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}

      <Dialog open={!!openCourse} onOpenChange={(o) => !o && setOpenCourse(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{openCourse?.title}</DialogTitle></DialogHeader>
          {openCourse && (
            <div className="space-y-2 mt-2">
              {(openCourse.modules || []).map((m, i) => {
                const done = (openCourse._enr?.completed_modules || []).includes(i);
                return (
                  <div key={i} className={`flex items-center justify-between p-3 rounded border ${done ? "bg-emerald-50 border-emerald-200" : "border-slate-200"}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${done ? "bg-emerald-500 text-white" : "bg-slate-200"}`}>
                        {done ? <CheckCircle2 className="h-4 w-4" /> : <span className="text-xs font-bold">{i + 1}</span>}
                      </div>
                      <span className="text-sm">{m}</span>
                    </div>
                    {!done && <Button size="sm" variant="outline" onClick={() => completeModule(openCourse.course_id, i)} data-testid={`complete-mod-${i}`}>Marquer fait</Button>}
                  </div>
                );
              })}
              {openCourse._enr?.certificate_issued_at && (
                <div className="mt-4 p-4 bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white rounded-xl text-center">
                  <Award className="h-8 w-8 mx-auto mb-2" />
                  <div className="font-display font-bold">Certificat délivré</div>
                  <div className="text-xs text-cyan-100 mt-1">{new Date(openCourse._enr.certificate_issued_at).toLocaleDateString("fr-FR")}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
